import JoyAutoComplete, {
  AutocompleteProps as JoyAutocompleteProps,
} from '@mui/joy/Autocomplete';
import { mergeProps, mergeRefs, useObjectRef } from '@react-aria/utils';
import { forwardRef } from 'react';
import { z } from 'zod';

// import { useFormController } from './form';

function isObject(value: unknown): value is object {
  return Object.prototype.toString.call(value) === '[object Object]';
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const hasId = (col: unknown): col is { id: any } => {
  return isObject(col) && 'id' in col && !!col.id;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const hasLabel = (col: unknown): col is { label: any } => {
  return isObject(col) && 'label' in col && !!col.label;
};

type ArrayItem<T> = T extends (infer U)[] ? U : never;

export const optionShapeSchema = z.object({
  label: z.coerce.string(),
  id: z.coerce.string(),
});
export const isOptionShape = (
  maybeOption: unknown,
): maybeOption is OptionShape =>
  optionShapeSchema.safeParse(maybeOption).success;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OptionShape = Record<string, any> & { label: string; id: string };
export const toPickerOptions = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ArrayData extends Array<any>,
  Item = ArrayItem<ArrayData>,
>(
  data: ArrayData,
  mappedId: (data: Item) => string,
  mappedLabel?: (data: Item) => string,
): OptionShape[] => {
  const mappedLabelFn =
    mappedLabel ??
    ((item: Item) =>
      hasLabel(item) && typeof item.label === 'string'
        ? item.label
        : 'No Label');

  return data
    .filter((item) => {
      if (mappedId(item)) {
        return true;
      }
      return false;
    })
    .reduce((options, item) => {
      if (hasId(item) || !!mappedId?.(item)) {
        options.push({
          label: mappedLabelFn(item),
          id: mappedId(item),
        });
      }
      return options;
    }, [] as OptionShape[]);
};

export const AutoComplete = forwardRef(function AutoComplete<
  Multiple extends boolean,
  DisableClear extends boolean,
  FreeSolo extends boolean,
>(
  {
    ...props
  }: JoyAutocompleteProps<OptionShape, Multiple, DisableClear, FreeSolo>,
  ref: React.ForwardedRef<HTMLInputElement>,
) {
  // const controller = useFormController();
  const objectRef = useObjectRef(ref);
  const mergedRef = mergeRefs(ref, objectRef);
  const mergedProps = mergeProps(props, {});
  return (
    <JoyAutoComplete
      {...mergedProps}
      slotProps={{
        ...props.slotProps,
        input: { ...(props.slotProps?.input ?? {}), ref: mergedRef },
      }}
    />
  );
});
